export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'lk-theme';
const THEME_COLORS = { light: '#f5f3ee', dark: '#0b0d0b' } as const;

export const getThemePreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // localStorage unavailable (private mode etc.)
  }
  return 'system';
};

const systemIsDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export const resolveTheme = (preference: ThemePreference): 'light' | 'dark' =>
  preference === 'system' ? (systemIsDark() ? 'dark' : 'light') : preference;

/** Apply the resolved theme to <html> and keep the browser chrome color in sync. */
export const applyTheme = (preference: ThemePreference) => {
  const resolved = resolveTheme(preference);
  document.documentElement.setAttribute('data-theme', resolved);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLORS[resolved]);
};

export const setThemePreference = (preference: ThemePreference) => {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Non-persistent is fine — the choice still applies for this visit.
  }
  applyTheme(preference);
};

/** Re-resolve when the OS scheme changes while preference is "system". */
export const watchSystemTheme = (getPreference: () => ThemePreference): (() => void) => {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getPreference() === 'system') applyTheme('system');
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
};
